/**
 * GLDN Gift Box Grouping — cart-page & cart-drawer box manager (gldn45 #334)
 *
 * Layers named gift-box GROUPING on top of the existing per-item gift-note
 * system (gldn-gift-note.js). The two are one system: a gift note IS a box.
 *
 * Model (operator-confirmed, additive — nothing in gldn-gift-note.js is
 * ripped out):
 *   • Clicking "Add gift box & message" on an item (owned by gldn-gift-note.js)
 *     makes that item a BOX with a message. Default name "Box 1" / "Box 2" …
 *     The box's message is the item's existing Gift / Gift Note properties —
 *     there is no separate per-item note.
 *   • Every OTHER eligible cart item shows "Add to gift box ▾" once at least
 *     one box exists, letting the shopper assign it into a box. Assignment
 *     writes ONE new line-item property `Gift Box: <name>` (the "new
 *     parameter" fulfillment/Odoo reads per line). A member has NO Gift/Gift
 *     Note of its own — it inherits the box's message.
 *   • Each item is in at most one box (single `Gift Box` value → AC-2).
 *   • Items never assigned = processed as normal (GLDN boxes them on their own
 *     by default). No property needed.
 *   • A summary of every box (name, message, assigned item titles) renders on
 *     the cart page AND in the cart drawer (AC-1, AC-3).
 *   • At checkout the box structure is serialized into cart.note (order notes)
 *     so fulfillment can pack (AC-4). This rides gldn-gift-note.js's single
 *     checkout interceptor — this file adds NO competing checkout listener.
 *
 * Data safety: line-item property writes go through the same "fetch /cart.js →
 * merge → write" path gldn-gift-note.js hardened (live bug order #G724590), so
 * adding `Gift Box` can never wipe personalizer / _unique / gift metadata.
 *
 * Rendering: this file adds NO MutationObserver of its own. gldn-gift-note.js
 * owns the cart observer; it calls window.gldnGiftBoxRender() after every
 * (re)inject. A cart:update listener + timed retries here are backups. Every
 * render is idempotent (bails when its DOM already matches state).
 */
(function () {
  'use strict';

  // Internal grouping token (owner's _unique). Underscore-prefixed so Shopify
  // hides it from the customer (cart / checkout / emails) — it's an opaque id,
  // not a packer- or shopper-readable value; fulfillment reads the human "Add to
  // box: N" line instead. Still visible in the admin/API for debugging. (#334.)
  var BOX_PROP = '_gldn_box';
  var GIFT_PROP = 'Gift';
  var NOTE_PROP = 'Gift Note';
  var DELIMITER = ' -|- ';
  // Per-line fulfillment indicator: the box NUMBER, written as a segment INSIDE
  // the `Gift Note` property (delimited by -|-), because GLDN's import tool
  // (Tailor) reads only the `Gift Note` line and imports everything after it into
  // Odoo — a separate property is invisible to it. So an owner's note becomes
  // "Gift To: F -|- Gift From: S -|- Gift Note: … -|- ADD TO BOX 1" and a member
  // (no To/From/Note of its own) carries "ADD TO BOX 1". Capitalized per production
  // request (Evan, Deb-approved); Tailor imports the raw text after "Gift Note:"
  // so the exact casing/format is cosmetic to Odoo. (gldn45 #334.)
  var BOX_SEG = 'ADD TO BOX';
  var MAX_NAME_LENGTH = 40;
  var NOTE_BLOCK_START = '[[GIFT BOXES]]';
  var NOTE_BLOCK_END = '[[/GIFT BOXES]]';
  // Shopify hard-caps cart.note at 5000 chars: /cart/update.js returns 410 Gone
  // (write REJECTED, not truncated) above it, which would silently leave a stale
  // box summary. Keep the serialized note under this with headroom (#334 QA M1).
  var NOTE_MAX_CHARS = 5000;
  var NOTE_SAFE_CHARS = 4800;

  var CART_CHANGE_MAX_RETRIES = 3;
  var CART_CHANGE_BACKOFF_MS = 400;

  /* ---- small shared helpers (kept local; ~parity with gldn-gift-note.js) --- */

  function postCartChange(body, attempt) {
    attempt = attempt || 0;
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (res.ok) return res.json();
      var transient = res.status === 429 || res.status === 502 ||
        res.status === 503 || res.status === 504;
      if (transient && attempt < CART_CHANGE_MAX_RETRIES) {
        var delay = CART_CHANGE_BACKOFF_MS * Math.pow(2, attempt);
        return new Promise(function (resolve) { setTimeout(resolve, delay); })
          .then(function () { return postCartChange(body, attempt + 1); });
      }
      throw new Error('Cart update failed: ' + res.status);
    });
  }

  // Retry transient failures (429/5xx) — under Shopify throttling /cart.js
  // returns an HTML challenge page, so an un-retried read would JSON-parse-throw
  // and the box write would silently no-op. Mirrors postCartChange's backoff.
  function getCart(attempt) {
    attempt = attempt || 0;
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (res) {
      if (res.ok) return res.json();
      var transient = res.status === 429 || res.status === 502 ||
        res.status === 503 || res.status === 504;
      if (transient && attempt < CART_CHANGE_MAX_RETRIES) {
        var delay = CART_CHANGE_BACKOFF_MS * Math.pow(2, attempt);
        return new Promise(function (resolve) { setTimeout(resolve, delay); })
          .then(function () { return getCart(attempt + 1); });
      }
      throw new Error('Cart read failed: ' + res.status);
    });
  }

  /** Lightweight failure toast (reuses gldn-gift-note's toast styling). Surfaced
   *  when a box operation can't persist, so it isn't a silent no-op. */
  function boxToast(message) {
    if (typeof document === 'undefined') return;
    var existing = document.querySelector('.gldn-gift-toast');
    if (existing) existing.remove();
    var toast = document.createElement('div');
    toast.className = 'gldn-gift-toast';
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'assertive');
    toast.textContent = message;
    document.body.appendChild(toast);
    requestAnimationFrame(function () { toast.classList.add('gldn-gift-toast--visible'); });
    setTimeout(function () {
      toast.classList.remove('gldn-gift-toast--visible');
      setTimeout(function () { if (toast.parentNode) toast.remove(); }, 300);
    }, 6000);
  }

  /** Parse "Gift To: X -|- Gift From: Y -|- Gift Note: Z" → {to,from,note}. */
  function parseGiftNote(raw) {
    var result = { to: '', from: '', note: '' };
    if (!raw) return result;
    raw.split(DELIMITER).forEach(function (part) {
      if (part.indexOf('Gift To:') === 0) result.to = part.replace('Gift To:', '').trim();
      else if (part.indexOf('Gift From:') === 0) result.from = part.replace('Gift From:', '').trim();
      else if (part.indexOf('Gift Note:') === 0) result.note = part.replace('Gift Note:', '').trim();
    });
    return result;
  }

  /** One-line human message from To/From/Note parts. */
  function displayParts(to, from, note) {
    var lead = [];
    if (to) lead.push('To ' + to);
    if (from) lead.push('from ' + from);
    var head = lead.join(', ');
    if (head && note) return head + ' — ' + note;
    return head || note || '';
  }

  /** One-line human message for a box, e.g. "To Mom, from Sam — Happy Birthday". */
  function formatMessage(raw) {
    var p = parseGiftNote(raw);
    return displayParts(p.to, p.from, p.note);
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function isEligibleRow(row) {
    if (!row) return false;
    if (row.classList.contains('cart-items__table-row--addon')) return false;
    if (row.getAttribute('data-gldn-gift-card') === 'true') return false;
    // Free / app-managed items (free ring sizer, BOGOS freebies) aren't gift-
    // boxable — gift-wrapping a $0 line is confusing and can race the free-gift
    // apps (#334 QA H3). Marked server-side by cart-products.liquid.
    if (row.getAttribute('data-gldn-free') === 'true') return false;
    if (row.getAttribute('data-gldn-ring-sizer') === 'true') return false;
    return !!row.getAttribute('data-key');
  }

  /** The recipient (gift "To") of a line, parsed from its Gift Note. */
  function recipientOf(item) {
    return parseGiftNote((item.properties || {})[NOTE_PROP] || '').to || '';
  }

  /** A line's stable per-line id (survives key rotation). Used as a box's
   *  grouping token so two boxes addressed to the SAME recipient name never
   *  merge — the token is the OWNER's identity, not its recipient text. */
  function uniqueOf(item) {
    var p = item.properties || {};
    return p._unique || p._random_id || '';
  }

  /* ---- The "ADD TO BOX N" segment lives INSIDE the Gift Note (Tailor reads
     only that property). These three helpers read/strip/append it, delimited by
     -|- like the To/From/Note segments. The MATCHER is case-insensitive and
     colon-optional so it still recognises the pre-capitalization format ("Add to
     box: 1") on carts saved before this change — they self-heal to "ADD TO BOX N"
     on the next reconcile. ---------------------------------------------------- */
  var BOX_SEG_RE = /^add to box\b\s*:?\s*(\d+)/i;   // captures the box number
  var BOX_SEG_PREFIX_RE = /^add to box\b/i;         // detects the segment to strip

  /** The box number encoded in a Gift Note's "ADD TO BOX N" segment (0 = none). */
  function parseBoxNum(rawNote) {
    var parts = (rawNote || '').split(DELIMITER);
    for (var i = 0; i < parts.length; i++) {
      var m = parts[i].trim().match(BOX_SEG_RE);
      if (m) { var n = parseInt(m[1], 10); if (n > 0) return n; }
    }
    return 0;
  }

  /** A Gift Note with any "ADD TO BOX X" segment removed (keeps To/From/Note). */
  function stripBoxSeg(rawNote) {
    return (rawNote || '').split(DELIMITER)
      .filter(function (p) { return p.trim() && !BOX_SEG_PREFIX_RE.test(p.trim()); })
      .join(DELIMITER);
  }

  /** A Gift Note whose box segment is set to `num` (null → removed). A member with
   *  no To/From/Note becomes just "ADD TO BOX N". Idempotent. */
  function noteWithBox(rawNote, num) {
    var base = stripBoxSeg(rawNote);
    if (num == null) return base;
    return base ? (base + DELIMITER + BOX_SEG + ' ' + num) : (BOX_SEG + ' ' + num);
  }

  /* --------------------------------------------------------------------------
   * Model — derive the set of boxes from the canonical cart.
   *
   * A box is identified by a stable `token` (the `Gift Box` line-item property
   * value — the OWNER's per-line id, `_unique`) so new members never split off
   * after the owner edits the recipient, AND two boxes addressed to the same
   * recipient name stay distinct (keying by recipient text would silently merge
   * them and drop one message). Its DISPLAY name is the owner's LIVE recipient
   * (the "To" field), so a rename shows immediately without any cascade write.
   * Each box also carries a 1-based `number` (its position in the cart) — the
   * human "Box 1 / Box 2 …" label and the value fulfillment reads per line.
   *
   * box = { token, number, name, ownerKey, message, memberKeys:[], persisted }
   *   owner  = the Gift:Yes line that started the box (carries the message)
   *   member = any line carrying that box's token
   * A solo owner not yet grouped is keyed by its own `_unique` (never recipient).
   * ------------------------------------------------------------------------ */
  function buildModel(cart) {
    var items = (cart && cart.items) || [];
    var boxes = {};   // token -> box
    var order = [];

    function ensure(token) {
      if (!boxes[token]) {
        boxes[token] = { token: token, name: '', ownerKey: null, message: '', memberKeys: [], persisted: false };
        order.push(token);
      }
      return boxes[token];
    }

    // Pass 1 — persisted membership (lines carrying a Gift Box token).
    items.forEach(function (it) {
      var props = it.properties || {};
      var token = props[BOX_PROP];
      if (token) {
        var box = ensure(token);
        box.persisted = true;
        box.memberKeys.push(it.key);
        if (props[GIFT_PROP] === 'Yes' && !box.ownerKey) {
          box.ownerKey = it.key;
          box.message = props[NOTE_PROP] || '';
        }
      }
    });

    // Pass 2 — solo owners (Gift:Yes, no token yet) → box keyed by the owner's
    // OWN per-line id, never the recipient text (so two solo gifts to the same
    // name are two boxes, not one merged box with a lost message).
    items.forEach(function (it) {
      var props = it.properties || {};
      if (props[GIFT_PROP] === 'Yes' && !props[BOX_PROP]) {
        var token = uniqueOf(it) || ('__gift-' + it.key); // stable per-line id
        var box = ensure(token);
        if (!box.ownerKey) { box.ownerKey = it.key; box.message = props[NOTE_PROP] || ''; }
        box.memberKeys.push(it.key);
      }
    });

    // Display name = owner's LIVE recipient (never the opaque id token).
    var titleByKey = {};
    items.forEach(function (it) { titleByKey[it.key] = it.product_title || it.title || ''; });
    order.forEach(function (token) {
      var box = boxes[token];
      var owner = items.filter(function (i2) { return i2.key === box.ownerKey; })[0];
      box.name = (owner && recipientOf(owner)) || '';
    });

    // Box numbers are COMPACT (1..N, no gaps) but adding a box never renumbers
    // existing ones. Each box carries a PERSISTED number (the "Add to box: N"
    // segment in its lines' Gift Note). We rank the already-persisted boxes by
    // that number and reassign them a compact 1..K in the SAME relative order
    // (so a dissolve's gap closes up on the next reconcile), then APPEND any
    // brand-new box (no segment yet) after them in cart order. Because new boxes
    // always land last, creating one leaves existing numbers unchanged; because
    // persisted boxes are re-ranked compactly, dissolving one shifts the rest
    // down with no gap. (gldn45 #334 — compact-after-dissolve per Carl.)
    var persistedBoxes = [], newBoxes = [];
    order.forEach(function (token) {
      var box = boxes[token];
      var persisted = 0;
      box.memberKeys.forEach(function (k) {
        if (persisted) return;
        var it = items.filter(function (i2) { return i2.key === k; })[0];
        var v = it && it.properties ? parseBoxNum(it.properties[NOTE_PROP]) : 0;
        if (v > 0) persisted = v;
      });
      box._persistedNum = persisted;
      (persisted > 0 ? persistedBoxes : newBoxes).push(box);
    });
    persistedBoxes.sort(function (a, b) { return a._persistedNum - b._persistedNum; });
    var n = 0;
    persistedBoxes.forEach(function (box) { box.number = ++n; delete box._persistedNum; });
    newBoxes.forEach(function (box) { box.number = ++n; delete box._persistedNum; });

    var boxByKey = {};
    order.forEach(function (token) {
      boxes[token].memberKeys.forEach(function (k) { boxByKey[k] = token; });
    });

    // Emit boxes in NUMERIC order (Box 1, Box 2, …) so the cart, the note, and
    // the assign dropdown always read in sequence — even though the box's cart
    // position (where its items sit) may differ from its stable number. Numbers
    // were assigned above (cart order for new boxes); sorting is display-only.
    var orderedBoxes = order.map(function (t) { return boxes[t]; })
      .sort(function (a, b) { return a.number - b.number; });

    return {
      cart: cart,
      boxes: orderedBoxes,
      boxByKey: boxByKey,       // line key -> box token
      titleByKey: titleByKey,
      tokens: orderedBoxes.map(function (b) { return b.token; })
    };
  }

  function boxByToken(model, token) {
    return model.boxes.filter(function (b) { return b.token === token; })[0] || null;
  }

  /* --------------------------------------------------------------------------
   * Cart snapshot cache — coalesce /cart.js reads across the render passes that
   * fire in bursts (init retries + cart:update). At most one in-flight fetch;
   * a short freshness window serves the last snapshot to piggybacking callers.
   * ------------------------------------------------------------------------ */
  var _cartCache = null;
  var _cartCacheAt = 0;
  var _cartInFlight = null;
  function snapshotCart(force) {
    var fresh = Date.now() - _cartCacheAt < 400;
    if (!force && _cartCache && fresh) return Promise.resolve(_cartCache);
    if (_cartInFlight) return _cartInFlight;
    _cartInFlight = getCart().then(function (cart) {
      _cartCache = cart; _cartCacheAt = Date.now(); _cartInFlight = null; return cart;
    }).catch(function (e) { _cartInFlight = null; throw e; });
    return _cartInFlight;
  }
  function invalidateCart() { _cartCache = null; _cartCacheAt = 0; }

  /** Patch DOM row + gift-cell keys from the fresh cart, matched on the stable
   *  _unique/_random_id property (data-gldn-unique). Used post-write instead of
   *  a morph. */
  function reconcileDomKeys(cart) {
    if (typeof document === 'undefined') return;
    var byUnique = {};
    var liveKeys = {};
    (cart.items || []).forEach(function (i) {
      liveKeys[i.key] = true;
      var u = i.properties && (i.properties._unique || i.properties._random_id);
      if (u) byUnique[u] = i.key;
    });
    document.querySelectorAll('.cart-items__table-row[data-key]').forEach(function (row) {
      var oldKey = row.getAttribute('data-key');
      if (liveKeys[oldKey]) return;                       // already current
      var u = row.getAttribute('data-gldn-unique');
      var newKey = u && byUnique[u];
      if (!newKey || newKey === oldKey) return;
      row.setAttribute('data-key', newKey);
      var cell = document.querySelector('.gldn-gift-cell[data-gldn-for-key="' + cssEscape(oldKey) + '"]');
      if (cell) cell.setAttribute('data-gldn-for-key', newKey);
    });
  }

  /** After a raw /cart/change.js write, line-item keys rotate. Rather than
   *  dispatch cart:update — which makes Horizon's cart-items-component re-fetch
   *  + morph the whole section, visibly disrupting an OPEN cart drawer (#334
   *  review item 3) — patch the affected DOM keys directly from the fresh cart
   *  (stable _unique match), exactly as gldn-gift-note.js patches row.data-key
   *  after its own saves. No morph → the drawer stays put. Then re-render. */
  /* --------------------------------------------------------------------------
   * Serialized op queue — EVERY box write (assign / remove / dissolve / the
   * create-box + gift-note-edit reconciles / the checkout flush) runs through
   * this ONE chain, so no two box operations ever overlap and race over the
   * cart's rotating line keys. That's the structural fix for the multi-box
   * dropouts: previously the assign, the owner-freeze, a debounced segment sync,
   * a debounced note write, and gldn-gift-note's own saves all fired
   * concurrently and clobbered each other.
   *
   * The UI is NOT blocked on the queue: a click renders optimistically and its
   * write just enqueues behind the current one, so the shopper can act again
   * immediately while the queue drains in the background (perf).
   * ------------------------------------------------------------------------ */
  var _opChain = Promise.resolve();
  // Set synchronously when a box OWNER is deleted via the native cart "Remove"
  // (which erases the line + its Gift Note atomically). We capture the owner's
  // message here and promote a survivor once Horizon's removal fires cart:update
  // — so the box survives with its message (gldn45 #334, M2 redesign).
  var _pendingPromotion = null;
  // Busy overlay: box ops (assign / remove / promote) run several serialized
  // writes + reconciles that visibly reshuffle the cart rows. Mask that churn with
  // a spinner overlay while the queue is draining (Carl r-M2). Delayed ~120ms so a
  // single fast op doesn't flash the overlay; hidden once the queue fully drains.
  var _busyCount = 0, _busyTimer = null;
  function enqueue(fn) {
    _busyCount++;
    if (!_busyTimer) _busyTimer = setTimeout(function () {
      _busyTimer = null;
      if (_busyCount > 0) showCartBusy();
    }, 120);
    var p = _opChain.then(fn, fn);
    _opChain = p.then(function () {}, function () {});
    var done = function () {
      _busyCount = Math.max(0, _busyCount - 1);
      if (_busyCount === 0) {
        if (_busyTimer) { clearTimeout(_busyTimer); _busyTimer = null; }
        hideCartBusy();
      }
    };
    p.then(done, done);
    return p;
  }

  /** Show a spinner overlay over the cart-items region while a box op runs. Append
   *  it INSIDE the cart-items container (not <body>) so it also covers the cart
   *  DRAWER, whose top-layer <dialog> would occlude a body-appended overlay. */
  function showCartBusy() {
    if (typeof document === 'undefined') return;
    var tables = document.querySelectorAll('.cart-items__table');
    for (var i = 0; i < tables.length; i++) {
      var host = tables[i].parentElement || tables[i];
      if (window.getComputedStyle(host).position === 'static') host.style.position = 'relative';
      var o = host.querySelector('.gldn-box-busy');
      if (!o) {
        o = document.createElement('div');
        o.className = 'gldn-box-busy';
        o.innerHTML = '<div class="gldn-box-busy__spinner" role="status" aria-label="Updating your cart"></div>';
        host.appendChild(o);
      }
      o.classList.add('gldn-box-busy--on');
    }
  }
  function hideCartBusy() {
    if (typeof document === 'undefined') return;
    var on = document.querySelectorAll('.gldn-box-busy--on');
    for (var i = 0; i < on.length; i++) on[i].classList.remove('gldn-box-busy--on');
  }

  /** Post-op UI refresh: patch DOM keys off the fresh cart (no morph → the
   *  drawer stays put) and re-render. No debounced background writers — segment
   *  writes happen inline in each queued op; cart.note is written at checkout. */
  function afterOp() {
    invalidateCart();
    return getCart().then(function (cart) {
      _cartCache = cart; _cartCacheAt = Date.now();
      reconcileDomKeys(cart);
      return renderNow(false);
    });
  }

  /** Ensure every boxed line's Gift Note carries the correct "Add to box: N"
   *  segment and every non-box line has none. The number rides INSIDE Gift Note
   *  (Tailor imports only that line). Writes ONE line per fresh fetch, reading
   *  its CURRENT props each time (never a stale snapshot), recursing until
   *  stable. MUST be called inside an enqueue()d op — it is not self-serializing;
   *  the queue guarantees no other box write runs concurrently. Never throws. */
  function reconcileSegments(pass) {
    pass = pass || 0;
    if (pass > 12) return Promise.resolve(null);       // safety bound
    return getCart().then(function (cart) {
      var model = buildModel(cart);
      var wantByKey = {};
      model.boxes.forEach(function (box) {
        box.memberKeys.forEach(function (k) { wantByKey[k] = box.number; });
      });
      var target = null;
      (cart.items || []).some(function (it) {
        var cur = (it.properties || {})[NOTE_PROP] || '';
        var want = wantByKey[it.key];                  // box number, or undefined
        var desired = want != null ? noteWithBox(cur, want) : stripBoxSeg(cur);
        if (desired !== cur) { target = { item: it, desired: desired }; return true; }
        return false;
      });
      if (!target) { _cartCache = cart; _cartCacheAt = Date.now(); return null; } // converged
      var props = Object.assign({}, target.item.properties || {});
      if (target.desired) props[NOTE_PROP] = target.desired;
      else delete props[NOTE_PROP];
      return postCartChange({ id: target.item.key, properties: props }).then(function () {
        invalidateCart();
        return reconcileSegments(pass + 1);
      });
    }).catch(function (err) {
      console.warn('gift-box segment reconcile failed (non-blocking):', err);
      return null;
    });
  }

  /** A box write couldn't persist (throttle/network). Surface it and re-sync the
   *  UI to the real cart instead of leaving a silent no-op (#334 QA H4). */
  function onWriteError(err) {
    console.warn('gift-box write failed:', err);
    boxToast("Couldn't update your gift box — please try again.");
    invalidateCart();
    return renderNow(true);
  }

  /* --------------------------------------------------------------------------
   * Writes
   * ------------------------------------------------------------------------ */

  /** Merge-write a single line's properties (preserves everything else).
   *
   *  Stale-key self-heal: the caller's itemKey comes from a DOM control's
   *  data-key, which can lag the live cart key when a nearby write rotated it
   *  mid-churn (e.g. assigning an item right after creating a box, before the
   *  key reconcile settled). A stale key isn't in the fresh cart, so the write
   *  would silently no-op — the item never joins the box (#334, Carl repro). If
   *  the key isn't found, re-resolve the line by the row's stable _unique
   *  (data-gldn-unique) and write to the LIVE key. */
  function writeProps(itemKey, mutate, uniqueHint) {
    return getCart().then(function (cart) {
      var item = (cart.items || []).find(function (i) { return i.key === itemKey; });
      // Prefer the caller's stable-id hint (captured at click time from the row's
      // data-gldn-unique) — reliable even when the DOM key is long stale and no
      // element still carries it. Falls back to a DOM lookup by the stale key.
      if (!item && uniqueHint) {
        item = (cart.items || []).find(function (i) {
          var p = i.properties || {};
          return (p._unique || p._random_id) === uniqueHint;
        });
      }
      if (!item && typeof document !== 'undefined') {
        var row = document.querySelector('.cart-items__table-row[data-key="' + cssEscape(itemKey) + '"]');
        var uniq = row && row.getAttribute('data-gldn-unique');
        if (uniq) {
          item = (cart.items || []).find(function (i) {
            var p = i.properties || {};
            return (p._unique || p._random_id) === uniq;
          });
        }
      }
      if (!item) return null;
      var props = Object.assign({}, item.properties || {});
      mutate(props);
      return postCartChange({ id: item.key, properties: props });
    });
  }

  /** Assign an item into the box identified by `token` (the OWNER's _unique).
   *  ONE write: set the member's `Gift Box` token. No owner-freeze write is
   *  needed — buildModel groups a member (token = owner._unique) with its owner
   *  (Gift:Yes, keyed by its own _unique) automatically, so the group forms
   *  without touching the owner (fewer writes, no owner-write race). The inline
   *  reconcile then stamps the member's "Add to box: N" segment. `uniqueHint` is
   *  the member row's stable _unique (captured at click time) so the write lands
   *  even if a concurrent op left the DOM key stale (#334). Runs on the queue. */
  function assignToBox(itemKey, token, uniqueHint) {
    return enqueue(function () {
      // Compute the target box number up front so the member's token AND its
      // "Add to box: N" segment land in ONE write (perf — no 2nd write for the
      // segment). reconcileSegments then only fixes the owner's segment if the
      // box was created without one (usually a no-op).
      return getCart().then(function (cart) {
        var box = boxByToken(buildModel(cart), token);
        var num = box ? box.number : null;
        return writeProps(itemKey, function (p) {
          p[BOX_PROP] = token;
          delete p[GIFT_PROP];                 // a member is not its own gift
          if (num != null) p[NOTE_PROP] = BOX_SEG + ' ' + num;    // member note = just the segment
          else delete p[NOTE_PROP];
        }, uniqueHint);
      })
        .then(function () { return reconcileSegments(0); })
        .then(afterOp).catch(onWriteError);
    });
  }

  /** Rebuild a Gift Note value from To/From/Note parts (matches gldn-gift-note's
   *  format). Used to capture a removed owner's message from its live gift-form
   *  fields when the cart cache is cold. The "Add to box: N" segment is re-applied
   *  by reconcileSegments after promotion, so it's omitted here. */
  function joinGiftParts(to, from, note) {
    var parts = [];
    to = (to || '').trim(); from = (from || '').trim(); note = (note || '').trim();
    if (to) parts.push('Gift To: ' + to);
    if (from) parts.push('Gift From: ' + from);
    if (note) parts.push('Gift Note: ' + note);
    return parts.join(DELIMITER);
  }

  /** Find a live cart line by key, falling back to the stable _unique hint. */
  function findLine(cart, key, uniqueHint) {
    var items = (cart && cart.items) || [];
    var it = items.filter(function (i) { return i.key === key; })[0];
    if (!it && uniqueHint) {
      it = items.filter(function (i) {
        var p = i.properties || {};
        return (p._unique || p._random_id) === uniqueHint;
      })[0];
    }
    return it || null;
  }

  /** Promote a surviving member of `token`'s box to owner so the box + its gift
   *  message persist when the previous owner leaves (unchecked, removed-from-box,
   *  or deleted from the cart). The promoted line takes the leaving owner's full
   *  Gift Note (`ownerNote` = To/From/Note + segment) and becomes Gift:Yes. Exactly
   *  ONE gift note per box is preserved (fulfillment unchanged — Deb confirmed no
   *  per-line replication). No survivor (solo box) → the box simply ends. MUST run
   *  inside an enqueue()d op. (gldn45 #334, M2 redesign — no un-removable owner.)
   *
   *  RE-KEYS the box to the survivor's own _unique. The old token was the REMOVED
   *  owner's _unique, and that removed item can later start a NEW box reusing that
   *  same _unique — which would collide with the box token and merge it back in
   *  with a second gift message (Carl repro). Keying to the survivor frees the old
   *  id, so the removed owner's new box is independent. */
  function promoteSurvivor(token, excludeKey, ownerNote) {
    if (!token) return Promise.resolve(null);
    return getCart().then(function (cart) {
      var survivor = (cart.items || []).filter(function (i) {
        var p = i.properties || {};
        return i.key !== excludeKey && p[BOX_PROP] === token && p[GIFT_PROP] !== 'Yes';
      })[0];
      if (!survivor) return null;               // solo box → nothing to promote
      var newToken = (survivor.properties || {})._unique || (survivor.properties || {})._random_id;
      return writeProps(survivor.key, function (p) {
        p[GIFT_PROP] = 'Yes';
        p[NOTE_PROP] = ownerNote;               // full To/From/Note (already carries the segment)
        delete p[BOX_PROP];                     // owner is identified by its OWN _unique now
      }, newToken).then(function () {
        return repointMembers(token, newToken); // move the box's other members onto the new token
      });
    });
  }

  /** Re-point every remaining member of a box from `oldToken` to `newToken` (the
   *  promoted owner's _unique) so the whole box shares the new key. Sequential —
   *  each write rotates the line key — recursing until none remain. Runs inside the
   *  promotion op. (gldn45 #334 — token re-key on promotion.) */
  function repointMembers(oldToken, newToken) {
    if (!oldToken || oldToken === newToken) return Promise.resolve(null);
    return getCart().then(function (cart) {
      var member = (cart.items || []).find(function (i) { return (i.properties || {})[BOX_PROP] === oldToken; });
      if (!member) return null;
      var hint = (member.properties || {})._unique || (member.properties || {})._random_id;
      return writeProps(member.key, function (p) { p[BOX_PROP] = newToken; }, hint)
        .then(function () { return repointMembers(oldToken, newToken); });
    });
  }

  /** Remove an item from its box (back to "ships on its own"). A MEMBER just has
   *  its token + "Add to box: N" segment stripped (its note is only that segment).
   *  An OWNER additionally gives up its Gift flag and message and, if any items
   *  remain in the box, PROMOTES a survivor to carry the message — so there is no
   *  un-removable "owner" and the box survives the owner leaving (gldn45 #334, M2
   *  redesign). Runs on the queue. */
  function removeFromBox(itemKey, uniqueHint) {
    return enqueue(function () {
      return getCart().then(function (cart) {
        var model = buildModel(cart);
        var item = findLine(cart, itemKey, uniqueHint);
        if (!item) return null;
        var isOwner = model.boxes.some(function (b) { return b.ownerKey === item.key; });
        var token = model.boxByKey[item.key];
        var ownerNote = isOwner ? ((item.properties || {})[NOTE_PROP] || '') : '';
        return writeProps(item.key, function (p) {
          delete p[BOX_PROP];
          if (isOwner) {
            delete p[GIFT_PROP];
            delete p[NOTE_PROP];                // owner gives up the message → moves to the survivor
          } else {
            var base = stripBoxSeg(p[NOTE_PROP]);
            if (base) p[NOTE_PROP] = base; else delete p[NOTE_PROP];
          }
        }, uniqueHint).then(function () {
          return isOwner ? promoteSurvivor(token, item.key, ownerNote) : null;
        });
      })
        .then(function () { return reconcileSegments(0); })  // renumber survivors if needed
        .then(afterOp).catch(onWriteError);
    });
  }

  /** Start a new box on an item by opening gldn-gift-note.js's message form
   *  (check its gift checkbox). The recipient the shopper types becomes the
   *  box name; no separate naming step. */
  function startNewBox(itemKey) {
    var cell = document.querySelector('.gldn-gift-cell[data-gldn-for-key="' + cssEscape(itemKey) + '"]');
    if (!cell) return;
    var checkbox = cell.querySelector('.gldn-gift-checkbox');
    if (checkbox && !checkbox.checked) {
      checkbox.checked = true;
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    }
    scheduleRender();          // swap the "Add to gift box" control for Cancel
    var to = cell.querySelector('.gldn-gift-to');
    if (to) to.focus();
  }

  /** Add a message to a message-less box by promoting its first member: open
   *  that member's gift-details form (it becomes the owner on Save) and pre-fill
   *  the recipient so the box's name carries over. */
  function addMessageToBox(itemKey, recipient) {
    var cell = document.querySelector('.gldn-gift-cell[data-gldn-for-key="' + cssEscape(itemKey) + '"]');
    if (!cell) return;
    var checkbox = cell.querySelector('.gldn-gift-checkbox');
    if (checkbox && !checkbox.checked) {
      checkbox.checked = true;
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    }
    var to = cell.querySelector('.gldn-gift-to');
    if (to && !to.value && recipient) { to.value = recipient; liveUpdateBoxHeader(cell); }
    scheduleRender();          // un-hide the promoted member's form (creatingBox)
    if (to) setTimeout(function () { to.focus(); }, 0);
  }

  /** Back out of a new-box gift form (uncheck the gift checkbox → gldn-gift-note
   *  closes the form). Returns the row to the "Add to gift box" options. */
  function cancelNewBox(itemKey) {
    var cell = document.querySelector('.gldn-gift-cell[data-gldn-for-key="' + cssEscape(itemKey) + '"]');
    if (!cell) return;
    var checkbox = cell.querySelector('.gldn-gift-checkbox');
    if (checkbox && checkbox.checked) {
      checkbox.checked = false;
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    }
    scheduleRender();
  }

  /* --------------------------------------------------------------------------
   * Checkout serialization → cart.note (called by gldn-gift-note.js's single
   * checkout interceptor via window.gldnGiftBoxSerializeNote). Merge-preserves
   * any pre-existing note by rewriting only our marker-delimited block.
   * ------------------------------------------------------------------------ */
  function buildNoteBlock(model, opts) {
    if (!model.boxes.length) return '';
    var compact = opts && opts.compact;
    var lines = [NOTE_BLOCK_START];
    model.boxes.forEach(function (box, i) {
      var msg = formatMessage(box.message);
      var label = 'Gift box ' + box.number + (box.name ? ' — for ' + box.name : '');
      lines.push(label + (msg ? ': ' + msg : ''));
      // Compact mode drops the per-member item titles: grouping already rides
      // each line's `Gift Box` property, so the item list is redundant for
      // fulfillment; the recipient + message are the note's unique payload.
      if (!compact) {
        box.memberKeys.forEach(function (k) {
          var t = model.titleByKey[k];
          if (t) lines.push('  - ' + t);
        });
      }
    });
    lines.push(NOTE_BLOCK_END);
    return lines.join('\n');
  }

  /** Cap the merged note to Shopify's 5000-char limit while preserving the
   *  block's END marker (so the next mergeNote still finds + replaces our
   *  block). Truncates on a line boundary and appends a visible marker. */
  function capNote(note) {
    if (note.length <= NOTE_MAX_CHARS) return note;
    var marker = '\n  …(truncated)\n' + NOTE_BLOCK_END;
    var keep = NOTE_MAX_CHARS - marker.length;
    var head = note.slice(0, keep);
    var lastNl = head.lastIndexOf('\n');
    if (lastNl > 0) head = head.slice(0, lastNl);
    // Drop any partial END marker left dangling in the head before re-appending.
    var danglingStart = head.lastIndexOf(NOTE_BLOCK_END);
    if (danglingStart !== -1) head = head.slice(0, danglingStart).replace(/\s+$/, '');
    return head + marker;
  }

  function mergeNote(existing, block) {
    var base = existing || '';
    var startIdx = base.indexOf(NOTE_BLOCK_START);
    if (startIdx !== -1) {
      var endIdx = base.indexOf(NOTE_BLOCK_END);
      var tail = endIdx !== -1 ? base.slice(endIdx + NOTE_BLOCK_END.length) : '';
      base = (base.slice(0, startIdx) + tail);
    }
    base = base.replace(/\n{3,}/g, '\n\n').trim();
    if (!block) return base;
    return (base ? base + '\n\n' : '') + block;
  }

  function serializeNote() {
    // Direct getCart() (not snapshotCart) so the note always reflects true
    // post-write state: snapshotCart(force) still returns an in-flight /cart.js
    // fetch that may have STARTED before the latest box write landed, which
    // under a rapid double-assign could serialize a pre-write cart (member
    // missing from the note). serialize is debounced (1200ms), so the extra
    // uncoalesced read is negligible. #334 QA M5.
    return getCart().then(function (cart) {
      var model = buildModel(cart);
      var block = buildNoteBlock(model);
      var note = mergeNote(cart.note || '', block);
      // Graceful degradation past Shopify's 5000-char cap (#334 QA M1): first
      // drop the redundant per-member item lines (compact), then hard-truncate
      // preserving markers, so the write always succeeds rather than 410-ing and
      // leaving a stale summary. Grouping still rides each line's `Gift Box`.
      if (note.length > NOTE_SAFE_CHARS) {
        note = mergeNote(cart.note || '', buildNoteBlock(model, { compact: true }));
      }
      if (note.length > NOTE_MAX_CHARS) {
        note = capNote(note);
      }
      if ((cart.note || '') === note) return null; // no change
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: note })
      });
    }).catch(function (err) {
      // Never block checkout on a note-serialization failure — the per-line
      // `Gift Box` property already carries grouping to fulfillment.
      console.warn('gift-box note serialization failed (proceeding):', err);
      return null;
    });
  }

  /* --------------------------------------------------------------------------
   * Render — per-row controls + the box summary. Idempotent.
   * ------------------------------------------------------------------------ */

  /** Boxes this item could join (all boxes except its own), labelled by
   *  recipient. Skips boxes whose recipient isn't set yet. */
  function otherBoxes(model, itemKey) {
    var own = model.boxByKey[itemKey];
    return model.boxes.filter(function (b) { return b.token !== own && b.name; });
  }

  /** Manage the inline "or add to gift box [dropdown]" that sits next to
   *  gldn-gift-note's "Add complimentary gift box & message" checkbox on an
   *  unassigned item — one consistent control. `show` is false while the
   *  gift-details form is open (checked) or when there are no boxes to join. */
  function renderInlineJoin(giftCell, model, key, show) {
    var check = giftCell.querySelector('.gldn-gift-check');
    if (!check) return;
    var join = check.querySelector('.gldn-box-inline-join');
    var joinable = show ? otherBoxes(model, key) : [];
    if (!joinable.length) { if (join) join.remove(); return; }
    var html =
      '<span class="gldn-box-assign-or">or</span>' +
      '<label class="gldn-box-assign-label" for="gldn-box-select-' + cssid(key) + '">add to gift box</label>' +
      '<select class="gldn-box-select" id="gldn-box-select-' + cssid(key) + '" data-key="' + escapeHtml(key) + '">' +
      '<option value="">Choose a box…</option>' +
      joinable.map(function (b) {
        // Label with the box NUMBER so two boxes to the same recipient name are
        // distinguishable ("Box 1 — Jeff", "Box 2 — Jeff").
        var label = 'Box ' + b.number + (b.name ? ' — ' + b.name : '');
        return '<option value="' + escapeHtml(b.token) + '">' + escapeHtml(label) + '</option>';
      }).join('') +
      '</select>';
    if (!join) {
      join = document.createElement('span');
      join.className = 'gldn-box-inline-join';
      var label = check.querySelector('label');
      if (label && label.nextSibling) check.insertBefore(join, label.nextSibling);
      else check.appendChild(join);
    }
    if (join.getAttribute('data-state') !== html) { join.innerHTML = html; join.setAttribute('data-state', html); }
  }

  /** Inject the per-row control into the row's gift cell. One elegant control:
   *  a recipient dropdown ("Add to gift box ▾ … / ＋ start a new one"), or a
   *  single "add gift box" link when no boxes exist, or a membership chip. */
  /** The owner's "Remove from this gift box" lives as a standalone control in its
   *  PRODUCT row (so it sits under the product like members), separate from its
   *  gift-details form which renderGrouping pins to the card bottom. */
  function ensureOwnerRemove(row, key) {
    var el = row.querySelector('.gldn-box-owner-remove');
    if (!el) {
      el = document.createElement('div');
      el.className = 'gldn-box-owner-remove';
      row.appendChild(el);
    }
    var html = '<button type="button" class="gldn-box-remove" data-key="' + escapeHtml(key) + '">Remove from this gift box</button>';
    if (el.getAttribute('data-state') !== html) { el.innerHTML = html; el.setAttribute('data-state', html); }
  }
  function removeOwnerRemove(row) {
    var el = row.querySelector('.gldn-box-owner-remove');
    if (el) el.remove();
  }
  /** After a morph-free promotion (uncheck / remove-from-box), the survivor is now
   *  Gift:Yes in the cart but its gift cell can still show the old unchecked MEMBER
   *  state — gift-note only rebuilds a cell on a morph, not on a property change.
   *  Sync the cell to the owner state (checked + saved form pre-filled from the
   *  transferred message) so the form shows. Only runs while the cell is stale
   *  (checkbox unchecked), so it never clobbers an owner mid-edit. (gldn45 #334.) */
  function syncOwnerCell(cell, message) {
    if (!cell) return;
    var cb = cell.querySelector('.gldn-gift-checkbox');
    if (!cb || cb.checked) return;                 // already showing owner state → leave it
    cb.checked = true;                             // visual only (no event → no gift-note save)
    var form = cell.querySelector('.gldn-gift-form'); if (form) form.classList.add('open', 'saved');
    var editBtn = cell.querySelector('.gldn-gift-edit'); if (editBtn) editBtn.style.display = '';
    var parsed = parseGiftNote(message || '');
    var to = cell.querySelector('.gldn-gift-to');
    if (to) { to.value = parsed.to; to.setAttribute('readonly', ''); }
    var from = cell.querySelector('.gldn-gift-from');
    if (from) { from.value = parsed.from; from.setAttribute('readonly', ''); }
    var note = cell.querySelector('.gldn-gift-textarea');
    if (note) { note.value = parsed.note; note.setAttribute('readonly', ''); }
  }
  function renderRowControl(row, model) {
    if (!isEligibleRow(row)) return;
    var key = row.getAttribute('data-key');

    // Stale-key self-heal: after a /cart/change.js write the line key rotates,
    // but the DOM row can briefly keep its old data-key. Rather than render
    // NOTHING (which made the "add to gift box" dropdown vanish and left it
    // carrying a dead key that no-op'd the next assign — #334 Carl repro),
    // re-resolve the LIVE key via the row's stable _unique and patch the row +
    // gift cell to it, so the control keeps working and points at the real line.
    if (!Object.prototype.hasOwnProperty.call(model.titleByKey, key)) {
      var uniq = row.getAttribute('data-gldn-unique');
      var live = uniq && (model.cart && model.cart.items || []).find(function (i) {
        var p = i.properties || {};
        return (p._unique || p._random_id) === uniq;
      });
      if (live && live.key !== key) {
        var oldKey = key; key = live.key;
        row.setAttribute('data-key', key);
        var oldCell = document.querySelector('.gldn-gift-cell[data-gldn-for-key="' + cssEscape(oldKey) + '"]');
        if (oldCell) oldCell.setAttribute('data-gldn-for-key', key);
      }
    }

    var giftCell = document.querySelector('.gldn-gift-cell[data-gldn-for-key="' + cssEscape(key) + '"]');
    if (!giftCell) return;

    // Still stale (no _unique match) → render NOTHING (not a wrong control)
    // until the key reconcile catches up. titleByKey holds exactly the current
    // cart keys.
    if (!Object.prototype.hasOwnProperty.call(model.titleByKey, key)) {
      giftCell.classList.remove('gldn-box-member', 'gldn-box-owner', 'gldn-box-unassigned');
      var stale = giftCell.querySelector('.gldn-box-control');
      if (stale && stale.getAttribute('data-state') !== '') { stale.innerHTML = ''; stale.setAttribute('data-state', ''); }
      return;
    }

    var token = model.boxByKey[key];
    var isOwner = model.boxes.some(function (b) { return b.ownerKey === key; });
    var isMember = !!token && !isOwner;
    var isUnassigned = !isOwner && !isMember;
    var joinable = otherBoxes(model, key);
    var checkbox = giftCell.querySelector('.gldn-gift-checkbox');
    var giftInProgress = !!(checkbox && checkbox.checked); // gift-details form open, not yet saved
    // Creating a box (new box on an unassigned item, or adding a message to a
    // message-less box by promoting a member) — the gift form is open and the
    // item isn't yet an owner. An OWNER editing its own message via "Edit Gift
    // Details" is not "creating" and keeps its normal (empty) control.
    var creatingBox = giftInProgress && !isOwner;

    giftCell.classList.toggle('gldn-box-member', isMember && !creatingBox);
    giftCell.classList.toggle('gldn-box-owner', isOwner);
    giftCell.classList.toggle('gldn-box-unassigned', isUnassigned && !giftInProgress);

    var control = giftCell.querySelector('.gldn-box-control');
    if (!control) {
      control = document.createElement('div');
      control.className = 'gldn-box-control';
      giftCell.appendChild(control);
    }

    var html = '';
    var showInlineJoin = false;
    if (isOwner) {
      // Owner: its "Remove from this gift box" is a STANDALONE control in the
      // PRODUCT row (under the product, matching members), while renderGrouping
      // pins the owner's gift-details FORM (this cell) to the bottom of the card.
      // So the cell's own control stays empty. (gldn45 #334, Carl r-M2.)
      ensureOwnerRemove(row, key);
      // A just-promoted owner's cell may still show the stale unchecked member
      // state — sync it to the owner form (no-op once already checked).
      var ownBox = null;
      model.boxes.forEach(function (b) { if (b.ownerKey === key) ownBox = b; });
      syncOwnerCell(giftCell, ownBox && ownBox.message);
      html = '';
    } else if (creatingBox) {
      removeOwnerRemove(row);
      // Gift-details form is open → the "or add to gift box" option is hidden
      // (renderInlineJoin(show:false) below) and a Cancel backs out (unchecking
      // the checkbox restores the option — #334 review r7).
      html =
        '<div class="gldn-box-cancel-row">' +
        '<button type="button" class="gldn-box-cancel" data-key="' + escapeHtml(key) + '">Cancel</button>' +
        '</div>';
    } else if (isMember) {
      removeOwnerRemove(row);
      // Members get "Remove from this gift box" in their own cell, under the item.
      html =
        '<div class="gldn-box-membership" data-testid="gift-box-membership">' +
        '<button type="button" class="gldn-box-remove" data-key="' + escapeHtml(key) + '">Remove from this gift box</button>' +
        '</div>';
    } else if (isUnassigned) {
      removeOwnerRemove(row);
      // gldn-gift-note's "Add complimentary gift box & message" checkbox stays
      // visible (it IS the create-a-box action); we append "or add to gift box
      // [dropdown]" beside it when there are boxes to join.
      showInlineJoin = joinable.length > 0;
    }
    renderInlineJoin(giftCell, model, key, showInlineJoin);
    // Only rewrite when the desired markup changed (avoid clobbering an open select).
    if (control.getAttribute('data-state') !== html) {
      control.innerHTML = html;
      control.setAttribute('data-state', html);
    }
  }

  /* --------------------------------------------------------------------------
   * In-cart grouping (#334 review item 2) — instead of a separate summary
   * section, group each box's REAL cart rows under a card-style box-header row.
   * Rows stay inside the cart-form table (checkout-safe); the header row mirrors
   * the existing `.cart-items__addons-header-row` pattern (a <tr><td colspan>).
   * ------------------------------------------------------------------------ */
  var HEADER_COLSPAN = 5;

  /** Split a tbody into line "blocks" — a main row plus its trailing add-on /
   *  add-ons-header rows (a builder set moves as one unit). Skips our own
   *  header rows. */
  function domBlocks(tbody) {
    var blocks = [];
    var current = null;
    Array.prototype.forEach.call(tbody.children, function (tr) {
      if (tr.classList.contains('gldn-box-header-row') || tr.classList.contains('gldn-box-footer-row')) return; // ours; rebuilt below
      var key = tr.getAttribute('data-key');
      var isAddon = tr.classList.contains('cart-items__table-row--addon');
      if (key && !isAddon) {
        current = { key: key, rows: [tr] };
        blocks.push(current);
      } else if (current) {
        current.rows.push(tr); // add-on / add-ons-header belongs to the current line
      } else {
        blocks.push({ key: null, rows: [tr] }); // stray leading row
      }
    });
    return blocks;
  }

  function buildBoxHeaderRow(box) {
    var tr = document.createElement('tr');
    tr.className = 'gldn-box-header-row';
    tr.setAttribute('role', 'row');
    tr.setAttribute('data-testid', 'gift-box-card');
    tr.setAttribute('data-box', box.token);
    var td = document.createElement('td');
    td.className = 'gldn-box-header-cell';
    td.setAttribute('colspan', String(HEADER_COLSPAN));
    // Header shows only "Box N · Gift for <recipient>" — the gift message /
    // To-From lives in the editable details at the bottom of the card, so it's
    // not duplicated up here (gldn45 #334).
    td.innerHTML =
      '<span class="gldn-box-card__recipient" data-testid="gift-box-number">Box ' + box.number + (box.name ? ' · Gift for ' : '') + '</span>' +
      '<span class="gldn-box-card__name" data-testid="gift-box-name">' + escapeHtml(box.name) + '</span>';
    tr.appendChild(td);
    return tr;
  }

  /** Footer row for a box with no message yet: an "Add complimentary gift box &
   *  message" affordance at the bottom of the card. Promotes the first member
   *  to the message owner (data-key) and pre-fills the recipient. */
  function buildBoxFooterRow(box, firstMemberKey) {
    var tr = document.createElement('tr');
    tr.className = 'gldn-box-footer-row';
    tr.setAttribute('role', 'row');
    var td = document.createElement('td');
    td.className = 'gldn-box-footer-cell';
    td.setAttribute('colspan', String(HEADER_COLSPAN));
    td.innerHTML =
      '<button type="button" class="gldn-box-add-message" data-key="' + escapeHtml(firstMemberKey) +
      '" data-recipient="' + escapeHtml(box.name) + '">＋ Add complimentary gift box &amp; message</button>';
    tr.appendChild(td);
    return tr;
  }

  /** Reorder each cart tbody so box members are contiguous under a card header.
   *  Idempotent: only re-lays-out a tbody when the desired child order differs
   *  from the current one (prevents an observer/reorder loop). */
  /** Signature of the desired grouped layout for a tbody — tokens, live names,
   *  messages, and the member/ungrouped key order. When it matches what's
   *  already applied AND the DOM order is intact, renderGrouping does NOTHING.
   *  This is what stops the mutation→observer→re-render reflow loop: an idle
   *  cart never mutates. */
  function groupSignature(groups, ungrouped) {
    var g = groups.map(function (grp) {
      return grp.box.token + '|' + grp.box.name + '|' + formatMessage(grp.box.message) + '|' +
        grp.rows.map(function (r) { return r.getAttribute('data-key') || ''; }).join('>');
    }).join('::');
    var u = ungrouped.map(function (r) { return r.getAttribute('data-key') || r.className; }).join(',');
    return g + '##' + u;
  }

  function renderGrouping(model) {
    document.querySelectorAll('.cart-items__table tbody').forEach(function (tbody) {
      var blocks = domBlocks(tbody);
      var byKey = {};
      blocks.forEach(function (b) { if (b.key) byKey[b.key] = b; });

      var groups = [];
      var used = {};
      model.boxes.forEach(function (box) {
        var memberBlocks = box.memberKeys.map(function (k) { return byKey[k]; }).filter(Boolean);
        if (!memberBlocks.length) return;
        // Order the OWNER's row FIRST — the message-holder leads the box card, so
        // the box reads top-down owner → members, and the owner's controls sit in
        // a predictable spot (gldn45 #334, Carl r-M2). CSS moves the owner's
        // "Remove from this gift box" link up under the product to match members.
        memberBlocks.sort(function (a, b) {
          if (a.key === box.ownerKey) return -1;
          if (b.key === box.ownerKey) return 1;
          return 0;
        });
        var rows = [];
        memberBlocks.forEach(function (b) { used[b.key] = true; b.rows.forEach(function (tr) { rows.push(tr); }); });
        groups.push({ box: box, rows: rows, mainKeys: memberBlocks.map(function (b) { return b.key; }) });
      });
      var ungrouped = [];
      blocks.forEach(function (b) { if (!(b.key && used[b.key])) b.rows.forEach(function (tr) { ungrouped.push(tr); }); });

      var CHROME = '.gldn-box-header-row, .gldn-box-footer-row';

      // No groups → tear down any prior grouping, but only if it exists (idempotent).
      if (!groups.length) {
        if (tbody.querySelector(CHROME)) Array.prototype.forEach.call(tbody.querySelectorAll(CHROME), function (h) { h.remove(); });
        if (tbody.querySelector('.gldn-box-grouped-row')) {
          Array.prototype.forEach.call(tbody.querySelectorAll('.gldn-box-grouped-row'), function (r) {
            r.classList.remove('gldn-box-grouped-row', 'gldn-box-grouped-row--first', 'gldn-box-grouped-row--last');
            if (r.getAttribute('data-testid') === 'assigned-cart-item') r.removeAttribute('data-testid');
          });
        }
        if (tbody.hasAttribute('data-testid')) tbody.removeAttribute('data-testid');
        if (tbody.hasAttribute('data-gldn-group-sig')) tbody.removeAttribute('data-gldn-group-sig');
        return;
      }

      var desiredRows = [];
      groups.forEach(function (g) { g.rows.forEach(function (r) { desiredRows.push(r); }); });
      desiredRows = desiredRows.concat(ungrouped);
      var footerCount = groups.filter(function (g) { return !formatMessage(g.box.message); }).length;

      var sig = groupSignature(groups, ungrouped);
      var currentRows = Array.prototype.filter.call(tbody.children, function (n) {
        return !n.classList.contains('gldn-box-header-row') && !n.classList.contains('gldn-box-footer-row');
      });
      var orderOk = currentRows.length === desiredRows.length && currentRows.every(function (n, i) { return n === desiredRows[i]; });
      var chromeOk = tbody.querySelectorAll('.gldn-box-header-row').length === groups.length &&
        tbody.querySelectorAll('.gldn-box-footer-row').length === footerCount;

      // Everything already in place → return without touching the DOM.
      if (orderOk && chromeOk && tbody.getAttribute('data-gldn-group-sig') === sig) return;

      // Mutating path: rebuild header/footer chrome, reorder rows, re-decorate.
      Array.prototype.forEach.call(tbody.querySelectorAll(CHROME), function (h) { h.remove(); });
      Array.prototype.forEach.call(tbody.querySelectorAll('.gldn-box-grouped-row'), function (r) {
        r.classList.remove('gldn-box-grouped-row', 'gldn-box-grouped-row--first', 'gldn-box-grouped-row--last');
        if (r.getAttribute('data-testid') === 'assigned-cart-item') r.removeAttribute('data-testid');
      });

      var frag = document.createDocumentFragment();
      var lastRowByOwnerKey = {};   // owner key → its box's last product row
      groups.forEach(function (g) {
        frag.appendChild(buildBoxHeaderRow(g.box));
        var hasFooter = !formatMessage(g.box.message);
        var lastMainRow = null;
        g.rows.forEach(function (r, i) {
          r.classList.add('gldn-box-grouped-row');
          if (i === 0) r.classList.add('gldn-box-grouped-row--first');
          // The card bottom is the footer when present, else the last item row.
          if (i === g.rows.length - 1 && !hasFooter) r.classList.add('gldn-box-grouped-row--last');
          if (r.getAttribute('data-key')) { r.setAttribute('data-testid', 'assigned-cart-item'); lastMainRow = r; }
          frag.appendChild(r);
        });
        // Pin the owner's gift-details FORM to the bottom of the card (Carl r-M2):
        // the owner is first, so its form belongs in the box's LAST product row.
        if (!hasFooter && g.box.ownerKey && lastMainRow) lastRowByOwnerKey[g.box.ownerKey] = lastMainRow;
        if (hasFooter) frag.appendChild(buildBoxFooterRow(g.box, g.mainKeys[0]));
      });
      ungrouped.forEach(function (r) { frag.appendChild(r); });
      tbody.appendChild(frag); // appendChild moves existing rows into order

      // gldn45 #334 (Carl r-M2): place EVERY gift cell so relocating an owner's
      // form never strands it. Each CURRENT owner's cell → its box's last row;
      // every OTHER cell → its own item's row (so an ex-owner that leaves the box
      // takes its gift form back, and the box doesn't keep the stale one); a cell
      // whose item is gone (a relocated owner cell left behind when its product was
      // removed) is dropped. gift-note re-injects anything genuinely missing.
      Array.prototype.forEach.call(tbody.querySelectorAll('.gldn-gift-cell'), function (cell) {
        var ckey = cell.getAttribute('data-gldn-for-key');
        if (!Object.prototype.hasOwnProperty.call(model.titleByKey, ckey)) { cell.remove(); return; }
        var dest = lastRowByOwnerKey[ckey] ||
          tbody.querySelector('.cart-items__table-row[data-key="' + cssEscape(ckey) + '"]');
        if (dest && cell.parentElement !== dest) dest.appendChild(cell);
      });

      tbody.setAttribute('data-testid', 'gift-box-summary'); // AC-3 container
      tbody.setAttribute('data-gldn-group-sig', sig);
    });
  }

  /* cssEscape / cssid — safe attribute + id fragments from a line key. */
  function cssEscape(s) {
    if (window.CSS && CSS.escape) return CSS.escape(s);
    return String(s).replace(/["\\]/g, '\\$&');
  }
  function cssid(s) { return String(s).replace(/[^a-zA-Z0-9]/g, '-'); }

  /** The stable per-line id (data-gldn-unique) of the cart row a control lives
   *  in — captured at click time so a write can re-resolve the live line even if
   *  its DOM key went stale mid-churn (#334 back-to-back-assign repro). The box
   *  controls are injected into the row's gift cell, so the row is an ancestor. */
  function uniqueForControl(el) {
    var row = el && el.closest && el.closest('.cart-items__table-row');
    return (row && row.getAttribute('data-gldn-unique')) || '';
  }

  /* --------------------------------------------------------------------------
   * Orchestration
   * ------------------------------------------------------------------------ */
  var _renderScheduled = false;
  function renderNow(force) {
    // gift-box.js loads globally (theme.liquid) so the cart DRAWER has the box
    // UI on every page. Bail before the /cart.js fetch when the page has no
    // cart rows and no summary to maintain (empty cart / no cart surface) —
    // mirrors gldn-gift-note.js's `if (!tbodies.length) return;` early exit.
    if (typeof document !== 'undefined' &&
        !document.querySelector('.cart-items__table-row[data-key]') &&
        !document.querySelector('[data-testid="gift-box-summary"]')) {
      return Promise.resolve(null);
    }
    return snapshotCart(force).then(function (cart) {
      var model = buildModel(cart);
      document.querySelectorAll('.cart-items__table-row[data-key]').forEach(function (row) {
        // Per-row guard: a control error on ONE row must NEVER abort the render —
        // otherwise the grouping (renderGrouping below) is skipped, so a
        // just-assigned item won't visibly join its box until a full page refresh
        // (Evan repro: "have to refresh for the cart to show it's in each box").
        // Surface it (console) so a genuine bug isn't swallowed silently.
        try { renderRowControl(row, model); }
        catch (e) { try { console.warn('gift-box: renderRowControl failed for a row (skipped):', e); } catch (x) {} }
      });
      try { renderGrouping(model); }
      catch (e) { try { console.warn('gift-box: renderGrouping failed:', e); } catch (x) {} }
      // renderNow is READ-ONLY — it never triggers cart writes. All segment /
      // cart.note writes happen inside the serialized op queue (box ops + the
      // gift-note-save reconcile + the checkout flush), so an idle cart, a
      // reload, or a native qty change never kicks off background writes (that
      // was the churn behind the slow, repeated re-renders).
      return model;
    }).catch(function (e) { try { console.warn('gift-box: render failed (cart read?):', e); } catch (x) {} });
  }

  function scheduleRender() {
    if (_renderScheduled) return;
    _renderScheduled = true;
    requestAnimationFrame(function () {
      _renderScheduled = false;
      renderNow(false);
    });
  }

  /** Optimistically update a box's header (name + message) from its owner's
   *  gift-note form as the recipient/message is edited, so "Gift for <name>"
   *  tracks the To field live — before the value is even saved. The
   *  signature-gated renderGrouping won't revert it (the SAVED value hasn't
   *  changed yet), and the post-save re-render rebuilds it from the saved cart. */
  // Write text via the element's text node (characterData mutation) rather than
  // textContent (a childList mutation) so gldn-gift-note.js's childList observer
  // doesn't re-inject on every keystroke.
  function setNodeText(el, text) {
    if (!el) return;
    if (el.firstChild && el.firstChild.nodeType === 3) {
      if (el.firstChild.nodeValue !== text) el.firstChild.nodeValue = text;
    } else if (el.textContent !== text) {
      el.textContent = text;
    }
  }

  function liveUpdateBoxHeader(cell) {
    var key = cell.getAttribute('data-gldn-for-key');
    if (!key) return;
    var row = document.querySelector('.cart-items__table-row[data-key="' + cssEscape(key) + '"]');
    if (!row) return;
    // Only track the header when this item is actually IN a box card (a grouped
    // row). While CREATING a new box the item isn't grouped yet (its card is
    // built on save), so walking back would land on a PREVIOUS card's header and
    // clobber it (#334 review r12). Also stop the walk if it crosses a footer or
    // an ungrouped row — that's a different card.
    if (!row.classList.contains('gldn-box-grouped-row')) return;
    var header = row.previousElementSibling;
    while (header &&
           !header.classList.contains('gldn-box-header-row') &&
           !header.classList.contains('gldn-box-footer-row') &&
           header.classList.contains('gldn-box-grouped-row')) {
      header = header.previousElementSibling;
    }
    if (!header || !header.classList.contains('gldn-box-header-row')) return;
    // The header shows only the recipient name now (no message line), so track
    // just the "To" field live as it's typed.
    var to = ((cell.querySelector('.gldn-gift-to') || {}).value || '').trim();
    setNodeText(header.querySelector('[data-testid="gift-box-name"]'), to);
  }

  /* The box-card header row that precedes a grouped row (its box card). */
  /* Close an owner's gift-details form UI (uncheck + collapse) after it leaves a
   *  box — its message has moved to the promoted survivor, so the form shouldn't
   *  linger open/checked. Visual only; the property writes happen in the op. */
  function clearGiftFormUI(cell) {
    if (!cell) return;
    var cb = cell.querySelector('.gldn-gift-checkbox'); if (cb) cb.checked = false;
    var form = cell.querySelector('.gldn-gift-form'); if (form) form.classList.remove('open', 'saved');
    var editBtn = cell.querySelector('.gldn-gift-edit'); if (editBtn) editBtn.style.display = 'none';
  }

  /* Event delegation for the controls (bound once). */
  function bindDelegates() {
    // CAPTURE phase — runs before gldn-gift-note's own (target-phase) checkbox
    // handler. Unchecking a gift-box OWNER must be handled by us (gldn-gift-note's
    // uncheck only clears Gift / Gift Note, which would leave an orphan box).
    // gldn45 #334 (M2 redesign): there is no un-removable owner — unchecking the
    // owner removes THAT item from the box and PROMOTES a survivor so the box and
    // its message persist (removeFromBox handles the promote). The last item out
    // ends the box. No dissolve, no warning — it's a normal, reversible action.
    document.addEventListener('change', function (e) {
      var cb = e.target.closest && e.target.closest('.gldn-gift-checkbox');
      if (!cb || cb.checked) return;                 // only intercept an UNCHECK
      var cell = cb.closest('.gldn-gift-cell');
      var key = cell && cell.getAttribute('data-gldn-for-key');
      if (!key) return;
      // Is this a box owner? Fast path: the rendered class. Fallback: the cart
      // cache (so an owner uncheck is still promoted if the box card hasn't painted
      // yet on a slow load). We only DECLINE to intercept when we can DEFINITIVELY
      // tell it's a plain gift item (cache warm, line present, not an owner) — then
      // gift-note's own uncheck is correct. When we can't tell (class missing AND
      // cache cold/absent), we intercept and let removeFromBox decide from a fresh
      // cart: it strips a plain item's gift too, so intercepting is never wrong,
      // and it never leaves the box orphaned (gldn45 #334, M2).
      var isOwner = cell.classList.contains('gldn-box-owner');
      var determinedPlain = false;
      if (!isOwner && _cartCache) {
        var uRow = cell.closest('.cart-items__table-row');
        var it = findLine(_cartCache, key, uRow && uRow.getAttribute('data-gldn-unique'));
        if (it) {
          isOwner = buildModel(_cartCache).boxes.some(function (b) { return b.ownerKey === it.key; });
          determinedPlain = !isOwner;
        }
      }
      if (determinedPlain) return;                     // definitely a plain gift item → gift-note handles
      e.stopImmediatePropagation();                   // block gldn-gift-note's uncheck handler
      e.preventDefault();
      clearGiftFormUI(cell);
      removeFromBox(key, uniqueForControl(cb));        // strip this owner (or plain gift) + promote a survivor
    }, true);

    document.addEventListener('change', function (e) {
      var sel = e.target.closest && e.target.closest('.gldn-box-select');
      if (sel) {
        var key = sel.getAttribute('data-key');
        var value = sel.value;
        if (key && value) {
          if (value === '__new__') { sel.value = ''; startNewBox(key); }
          else assignToBox(key, value, uniqueForControl(sel));
        }
        return;
      }
      // Recipient/message edited → update the box header live (#334 review r3).
      var field = e.target.closest && e.target.closest('.gldn-gift-to, .gldn-gift-from, .gldn-gift-textarea');
      if (field) {
        var giftCell = field.closest('.gldn-gift-cell');
        if (giftCell) liveUpdateBoxHeader(giftCell);
      }
      // Checking/unchecking the gift checkbox toggles the form; re-render so the
      // "or add to gift box" option hides (checked) or restores (unchecked).
      var cb = e.target.closest && e.target.closest('.gldn-gift-checkbox');
      if (cb) scheduleRender();
    });
    // Live-track the recipient as it is typed (not just on blur/commit).
    document.addEventListener('input', function (e) {
      var field = e.target.closest && e.target.closest('.gldn-gift-to, .gldn-gift-from, .gldn-gift-textarea');
      if (!field) return;
      var giftCell = field.closest('.gldn-gift-cell');
      if (giftCell) liveUpdateBoxHeader(giftCell);
    });
    // gldn45 #334 (M2): the native cart "Remove" deletes a line AND its Gift Note
    // in one shot, so a removed OWNER's message can't be recovered afterward. If
    // the line being removed is a box owner with survivors, capture its message
    // now (synchronously, from the cart cache) and promote a survivor once
    // Horizon's removal fires cart:update. We do NOT block the native removal —
    // Horizon animates + morphs the row; we only recover the box on the update.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('button');
      if (!btn || (btn.getAttribute('on:click') || '').indexOf('/onLineItemRemove/') !== 0) return;
      var row = btn.closest('.cart-items__table-row');
      if (!row) return;
      // Is the line being removed a box OWNER? An owner shows a CHECKED gift
      // checkbox (gift-note renders that regardless of our own render timing), so
      // this works even before the box card has painted on a slow load. Members /
      // plain items aren't checked → their native removal is already clean.
      var cell = document.querySelector('.gldn-gift-cell[data-gldn-for-key="' + cssEscape(row.getAttribute('data-key') || '') + '"]')
              || row.querySelector('.gldn-gift-cell');
      var cb = cell && cell.querySelector('.gldn-gift-checkbox');
      if (!cb || !cb.checked) return;
      // Capture the owner's message BEFORE Horizon deletes the line (the delete
      // erases its Gift Note too): prefer the cart cache, fall back to the live
      // gift-form fields so it works even when the cache is cold.
      var ownerNote = '';
      var cached = _cartCache && findLine(_cartCache, row.getAttribute('data-key'), row.getAttribute('data-gldn-unique'));
      if (cached) ownerNote = (cached.properties || {})[NOTE_PROP] || '';
      if (!ownerNote) ownerNote = joinGiftParts(
        (cell.querySelector('.gldn-gift-to') || {}).value,
        (cell.querySelector('.gldn-gift-from') || {}).value,
        (cell.querySelector('.gldn-gift-textarea') || {}).value
      );
      // Promote a survivor on the follow-up cart:update, once the owner line is
      // gone (a solo box just ends — the promote is a no-op then). Auto-clear so a
      // stray pending never lingers. (gldn45 #334, M2.)
      _pendingPromotion = { ownerNote: ownerNote };
      setTimeout(function () { _pendingPromotion = null; }, 6000);
    }, true);

    document.addEventListener('click', function (e) {
      var remove = e.target.closest && e.target.closest('.gldn-box-remove');
      if (remove) {
        var rk = remove.getAttribute('data-key');
        // Owner leaving via the link → close its (checked) gift form so it doesn't
        // linger; removeFromBox promotes a survivor so the box survives.
        var rCell = document.querySelector('.gldn-gift-cell[data-gldn-for-key="' + cssEscape(rk) + '"]');
        if (rCell && rCell.classList.contains('gldn-box-owner')) clearGiftFormUI(rCell);
        removeFromBox(rk, uniqueForControl(remove));
        return;
      }
      var start = e.target.closest && e.target.closest('.gldn-box-start');
      if (start) { startNewBox(start.getAttribute('data-key')); return; }
      var cancel = e.target.closest && e.target.closest('.gldn-box-cancel');
      if (cancel) { cancelNewBox(cancel.getAttribute('data-key')); return; }
      var addMsg = e.target.closest && e.target.closest('.gldn-box-add-message');
      if (addMsg) { addMessageToBox(addMsg.getAttribute('data-key'), addMsg.getAttribute('data-recipient')); return; }
      // After gldn-gift-note saves the gift message (this creates/edits a box),
      // enqueue a reconcile so the owner's "Add to box: N" segment is (re)applied
      // — gift-note's save rewrites Gift Note WITHOUT the segment. Serialized
      // behind any in-flight box op; re-reads fresh so it never reverts the save.
      var save = e.target.closest && e.target.closest('.gldn-gift-save');
      if (save) {
        setTimeout(function () {
          enqueue(function () { return reconcileSegments(0).then(afterOp); });
        }, 900);   // let gift-note's own save write land first
      }
    });
  }

  /* --------------------------------------------------------------------------
   * Public surface + wiring
   *
   * Browser-only. Guarded with typeof checks so the pure box-model functions
   * below can be required + unit-tested under Node without a DOM (the guards
   * are inert in the browser, where window/document always exist).
   * ------------------------------------------------------------------------ */
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    // Called by gldn-gift-note.js after each (re)inject so box controls land in
    // the freshly-rendered gift cells, and by our own backups below.
    window.gldnGiftBoxRender = function () { scheduleRender(); };
    // Called by gldn-gift-note.js's checkout interceptor before it redirects.
    // Checkout flush — both run through the op queue so they're serialized behind
    // any in-flight box op and each other (reconcile first, then note). The
    // interceptor awaits syncIndicators() then serializeNote() in that order.
    window.gldnGiftBoxSyncIndicators = function () {
      return enqueue(function () { return reconcileSegments(0); });
    };
    window.gldnGiftBoxSerializeNote = function () {
      return enqueue(function () { return serializeNote(); });
    };

    bindDelegates();

    // Initial-paint safety net (Carl r-M2). gift-note calls gldnGiftBoxRender()
    // after it injects gift cells, but on a slow load that can land before the
    // cart rows/cells are in the DOM, leaving the box card unpainted until a
    // cart:update. Observe the cart tables for row/cell insertions and (re)render.
    // Idempotent (renderGrouping is signature-gated), so it settles at once — no
    // reflow loop. Fires on node insertion only, never on typing.
    var _cartObserver = (typeof MutationObserver !== 'undefined') ? new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if ((n.classList && (n.classList.contains('gldn-gift-cell') || n.classList.contains('cart-items__table-row'))) ||
              (n.querySelector && n.querySelector('.gldn-gift-cell, .cart-items__table-row[data-key]'))) {
            scheduleRender();
            return;
          }
        }
      }
    }) : null;
    var observeCart = function () {
      if (!_cartObserver) return;
      document.querySelectorAll('.cart-items__table tbody').forEach(function (tb) {
        _cartObserver.observe(tb, { childList: true, subtree: true });
      });
    };

    var boot = function () {
      getCart();                 // warm _cartCache so sync handlers (owner uncheck /
                                 // native remove) can read box state before first paint
      observeCart();
      scheduleRender();
      setTimeout(scheduleRender, 150);
      setTimeout(scheduleRender, 600);
      setTimeout(scheduleRender, 1500);
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
    window.addEventListener('load', function () { scheduleRender(); setTimeout(scheduleRender, 200); });
    document.addEventListener('cart:update', function () {
      invalidateCart();
      observeCart();             // re-attach if Horizon morphed the cart tbody
      requestAnimationFrame(scheduleRender);
      setTimeout(scheduleRender, 200);
      setTimeout(scheduleRender, 700);
      // A box owner was just deleted via the native cart "Remove" — promote a
      // survivor (with the message we captured before the line was erased) so the
      // box survives instead of orphaning its members (gldn45 #334, M2). The
      // orphaned box is the one that now has members but no owner. Consumed only
      // once a promotion actually lands, so a cart:update that fires before
      // Horizon's removal reflects doesn't waste the pending capture.
      if (_pendingPromotion) {
        var pp = _pendingPromotion;
        enqueue(function () {
          return getCart().then(function (cart) {
            var orphaned = buildModel(cart).boxes.filter(function (b) {
              return !b.ownerKey && b.memberKeys.length;
            })[0];
            if (!orphaned) return null;               // solo box ended, or removal not reflected yet
            _pendingPromotion = null;                 // consumed
            return promoteSurvivor(orphaned.token, null, pp.ownerNote);
          }).then(function () { return reconcileSegments(0); }).then(afterOp);
        });
      }
    });
    document.addEventListener('dialog:open', function (e) {
      var t = e.target;
      if (!t || !(t.closest && t.closest('cart-drawer-component'))) return;
      setTimeout(scheduleRender, 100);
      setTimeout(scheduleRender, 350);
    });
  }

  // Node-only test surface (inert in the browser — `module` is undefined there).
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { buildModel: buildModel, mergeNote: mergeNote, buildNoteBlock: buildNoteBlock, capNote: capNote, formatMessage: formatMessage, parseGiftNote: parseGiftNote, parseBoxNum: parseBoxNum, stripBoxSeg: stripBoxSeg, noteWithBox: noteWithBox, NOTE_MAX_CHARS: NOTE_MAX_CHARS, NOTE_BLOCK_END: NOTE_BLOCK_END, BOX_SEG: BOX_SEG };
  }
})();
